import { Button, FormControl, FormHelperText, FormLabel, Input, Stack, Typography } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useId } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { FloatingInput } from '@/components/form-elements/FloatingInput';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { SignCodeAutocomplete } from '@/components/form-elements/SignCodeAutocomplete';
import { ButtonLink } from '@/components/router/ButtonLink';
import { SignCodeIcon } from '@/components/signs/SignCodeIcon';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { SignDeleteOption } from '@/components/signs/modal/SignDeleteOption';
import { useAppAbility } from '@/hooks/useAppAbility';
import { I18N_DE } from '@/i18n/de';
import {
  SignCode,
  SignCondition,
  SignPermanency,
  SignReflectionClass,
  SignSize,
  SignSourceType,
  UpdateSignDto,
} from '@/modules/api';
import { backendApiSignsUpdateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useSignCodeQuery } from '@/queries/useSignCodeQuery';
import { useSignQuery } from '@/queries/useSignQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId_/$signGroupId_/$signId/bearbeiten',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: SignDetailsRoute,
});

function SignDetailsRoute() {
  const { adminLevelId, signSupportId, signGroupId, signId } = Route.useParams();
  const navigate = Route.useNavigate();
  const formId = useId();
  const { reloadSupportLayer } = useCreateSignStore((state) => state.actions);
  const { ability } = useAppAbility();

  const { data: sign } = useSignQuery();
  const { data: dataSignCode } = useSignCodeQuery(sign?.code || '');

  const {
    control,
    watch,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<
    Pick<UpdateSignDto, 'size' | 'reflectionClass' | 'value' | 'permanency' | 'condition'> & {
      signCode: SignCode;
    }
  >();

  useEffect(
    () =>
      reset({
        ...sign,
        signCode: {
          ...sign,
          ...(dataSignCode && { description: dataSignCode.description }),
        },
      }),
    [sign, reset, dataSignCode],
  );

  const signCode = watch('signCode');

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignsUpdateMutation(),
    onSuccess: async () => {
      reloadSupportLayer();
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId/$signId',
        params: {
          adminLevelId,
          signSupportId,
          signGroupId,
          signId,
        },
      });
    },
  });

  return (
    sign && (
      <EditModalDialog title="Verkehrszeichen bearbeiten">
        <Stack
          component="form"
          id={formId}
          onSubmit={handleSubmit(({ signCode: { code }, size, reflectionClass, value, permanency, condition }) => {
            if (isPending) return;

            mutate({
              path: { signId: sign.id },
              body: {
                code,
                source: SignSourceType.MANUAL,
                size,
                reflectionClass,
                value,
                permanency,
                condition,
              },
            });
          })}
          gap={2}
        >
          <FormControl error={!!errors.signCode}>
            <FormLabel>Verkehrszeichen</FormLabel>
            {sign.status === 'DRAFT' ? (
              <Controller
                control={control}
                name="signCode"
                rules={{ required: true }}
                defaultValue={dataSignCode}
                render={({ field }) => (
                  <SignCodeAutocomplete
                    defaultValue={dataSignCode}
                    onChange={(event, value) => {
                      field.onChange(value);
                    }}
                  />
                )}
              />
            ) : (
              <Stack direction="row" gap={2}>
                <Input value={sign.code} readOnly variant="outlined" />
                {ability.can('CREATE', 'Process') && (
                  <ButtonLink
                    variant="plain"
                    to="/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId"
                    params={{ adminLevelId, signSupportId, signGroupId }}
                    search={{ openProcess: true }}
                  >
                    Verkehrszeichen ändern
                  </ButtonLink>
                )}
              </Stack>
            )}
          </FormControl>

          {signCode && (
            <Stack direction="row" gap={1} alignItems="center">
              <SignCodeIcon code={signCode.code} style={{ width: 40, height: 40 }} />
              <Typography level="body-sm" sx={{ fontSize: 14, textWrap: 'nowrap' }}>
                {signCode.code}
              </Typography>
              <Typography level="body-sm" sx={{ fontSize: 14, fontWeight: 700 }}>
                {signCode.description}
              </Typography>
            </Stack>
          )}

          <FormControl error={!!errors.size}>
            <FormLabel>Größe</FormLabel>
            <Controller
              control={control}
              name="size"
              defaultValue={undefined}
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignSize)}
                  optionLabel={({ option }) => I18N_DE.SignSize[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.reflectionClass}>
            <FormLabel>Reflektionsklasse</FormLabel>
            <Controller
              control={control}
              name="reflectionClass"
              defaultValue={undefined}
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignReflectionClass)}
                  optionLabel={({ option }) => I18N_DE.SignReflectionClass[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.permanency}>
            <FormLabel>Beschilderungsart</FormLabel>
            <Controller
              control={control}
              name="permanency"
              defaultValue={undefined}
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignPermanency)}
                  optionLabel={({ option }) => I18N_DE.SignPermanency[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.condition}>
            <FormLabel>Zustand</FormLabel>
            <Controller
              control={control}
              name="condition"
              defaultValue={undefined}
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignCondition)}
                  optionLabel={({ option }) => I18N_DE.SignCondition[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.value}>
            <FormLabel>Zusatz</FormLabel>
            <Input
              type="text"
              variant="outlined"
              slots={{
                input: FloatingInput,
              }}
              slotProps={{
                input: {
                  ...register('value'),
                  label: 'optional',
                },
              }}
            />
          </FormControl>

          {error && <FormHelperText>{error.message}</FormHelperText>}

          <Stack direction="row" gap={2}>
            <Button type="submit" form={formId} disabled={!signCode || isPending} loading={isPending}>
              Speichern
            </Button>

            <SignDeleteOption sign={sign} />
          </Stack>
        </Stack>
      </EditModalDialog>
    )
  );
}
